import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { PageHead, StatusLabel, money, when } from "@/studio/ui";
import { useStartStage } from "@/studio/ProjectPanels";

const STATS = [
  ["active_clients", "Active clients"],
  ["active_projects", "Active projects"],
  ["pending_deposits", "Pending deposits"],
  ["incomplete_consultations", "Incomplete consultations"],
];
const NextButton = ({ p, reload }) => {
  const start = useStartStage();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const na = p.next_action;
  if (na.kind === "none") return <span className="st-tag st-tag--ok" data-testid={`next-action-${p.id}`}>{na.label}</span>;
  const go = async () => {
    if (na.kind === "consultation") return start(p.id, na.stage);
    if (na.kind === "api") {
      setBusy(true);
      try { await adminApi[na.api.method](na.api.path, na.api.body); toast.success(`${na.label} — done`); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setBusy(false);
      return null;
    }
    return navigate(`/admin/studio/projects/${p.id}?tab=${na.tab || "overview"}`);
  };
  return <button className="st-btn st-btn--sm" disabled={busy} onClick={go} data-testid={`next-action-${p.id}`}>{busy ? "Working…" : na.label}</button>;
};

const NeedsAction = ({ items, reload }) => (
  <section className="st-panel" data-testid="dashboard-needs-attention">
    <div className="st-panel__head"><h2 className="st-h2">Projects · next action</h2><span className="st-muted st-small">{items.length} projects</span></div>
    {items.length === 0 ? <p className="st-empty">Nothing waiting on you.</p> : (
      <ul className="st-list">
        {items.map((p) => (
          <li key={p.id}>
            <div className="st-row" style={{ flexWrap: "wrap" }} data-testid={`attention-item-${p.id}`}>
              <div style={{ minWidth: 0 }}>
                <Link to={`/admin/studio/projects/${p.id}`} className="st-row__title" data-testid={`attention-link-${p.id}`}>{p.name}</Link>
                <div className="st-row__meta" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span>{p.client_name} · {p.pending ? "Fee pending" : money(p.total)}</span><StatusLabel status={p.status} /><span className="st-small" data-testid={`attention-progress-${p.id}`}>{p.progress.done}/{p.progress.total} steps</span>
                </div>
                {p.reasons.length > 0 && <div className="st-muted st-small" style={{ marginTop: 4 }} data-testid={`attention-reasons-${p.id}`}>{p.reasons.join(" · ")}</div>}
              </div>
              <NextButton p={p} reload={reload} />
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default function StudioDashboard() {
  const [data, setData] = useState(null);
  const load = () => adminApi.get("/studio/dashboard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <p className="st-loading">Loading…</p>;

  return (
    <div className="st-page" data-testid="studio-dashboard">
      <PageHead eyebrow="hausA Studio" title="Studio Overview" lead="What needs doing next, then where everything stands.">
        <Link to="/admin/studio/clients?new=1" className="st-btn" data-testid="dashboard-add-client-button">Add New Client</Link>
      </PageHead>
      <NeedsAction items={data.needs_attention} reload={load} />
      <div className="st-stats st-stats--compact" style={{ marginTop: 24 }} data-testid="dashboard-stats">
        {STATS.map(([k, label]) => (
          <div className="st-stat" key={k}>
            <div className="st-stat__n" data-testid={`dashboard-stat-${k}`}>{data.counts[k]}</div>
            <div className="st-stat__l">{label}</div>
          </div>
        ))}
      </div>
      <section className="st-panel" data-testid="dashboard-recent-activity">
        <div className="st-panel__head"><h2 className="st-h2">Recent activity</h2></div>
        {data.recent_activity.length === 0 ? <p className="st-empty">No activity yet.</p> : (
          <ul className="st-list st-activity st-activity--compact">
            {data.recent_activity.map((a) => (
              <li key={a.id}>
                <span><strong style={{ fontWeight: 600 }}>{a.action}</strong>{a.detail ? <span className="st-muted"> — {a.detail}</span> : null}</span>
                <time>{when(a.timestamp)}</time>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
